import React from "react";
import { Handshake, Target, Globe2, Check } from "lucide-react";
import { PageHero, Reveal, Stagger, StaggerItem, Overline, SectionHeading, CTAButton } from "@/components/primitives";
import { TagGrid, SponsorProposalBand } from "@/components/sections";
import LeadDialog, { DOCS } from "@/components/forms/LeadDialog";
import { EXPO_EXHIBITORS, EXPO_VISITORS, EVENT } from "@/data/site";

const WHY = [
  { icon: Handshake, t: "Direct buyer access", d: "Pre-scheduled meetings with producers, OMCs and project developers." },
  { icon: Target, t: "Project pipeline", d: "EPC, licensing, equipment and JV enquiries from live projects." },
  { icon: Globe2, t: "India market entry", d: "The cleanest single way in for an overseas firm." },
];

export default function Expo() {
  return (
    <>
      <PageHero
        overline="Concurrent expo"
        title="India is building. Bring the technology."
        subtitle={`${EVENT.expo} — a one-day technology marketplace, 28 September, alongside the forum.`}
        image="/assets/site/expo-busy-trade-floor.jpg"
        imageFallback="https://images.unsplash.com/photo-1531058020387-3be344556be6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
      />

      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <Overline className="mb-4">What the expo is</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">A working floor, not a showroom.</h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-navy/70">
              <p>The {EVENT.expo} runs alongside the conference for one focused day. It's where the technology that will build India's next phase of ethanol capacity — plant equipment, process technology, automation and engineering — meets the producers, project developers and investors actually planning to buy it.</p>
              <p>India now produces more ethanol than its current vehicles can absorb, with over 45% coming from grain. New distilleries, 2G plants, flex-fuel, compressed biogas and aviation fuel are closing that gap — and every one of them is a buyer for plant technology, equipment, automation and engineering.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Who exhibits" title="Every layer of the biofuel stack." />
          <Stagger className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {EXPO_EXHIBITORS.map((e) => (
              <StaggerItem key={e}>
                <div className="rounded-xl border border-border bg-parchment px-5 py-4 font-medium text-navy/80 transition-colors hover:border-emerald2/40 hover:text-emeraldDeep">{e}</div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Who visits" title="Buyers with budgets, not browsers." />
          <Reveal className="mt-10"><TagGrid tags={EXPO_VISITORS} /></Reveal>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Why exhibit" title="One day. The whole demand side in one place." />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
            {WHY.map((w) => (
              <StaggerItem key={w.t}>
                <div className="flex h-full flex-col rounded-3xl border border-border bg-parchment p-8 transition-all hover:-translate-y-1 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep"><w.icon className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-xl font-bold text-navy">{w.t}</h3>
                  <p className="mt-2 text-navy/60">{w.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FIRST EDITION — humble */}
      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <Overline className="mb-4">The first edition</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">Get in before everyone else does.</h2>
            <p className="mt-6 text-lg leading-relaxed text-navy/70">
              First editions are where the early relationships get made. As a new, focused expo we can offer exhibitors something a crowded show can't — real visibility, real conversations, and a seat at the table as India's ethanol-technology market takes shape. We're being selective about who's on the floor so that every meeting counts.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-3">
              {["A curated, manageable floor", "Direct introductions to buyers", "Founding-exhibitor recognition"].map((x) => (
                <li key={x} className="flex items-start gap-2 rounded-2xl border border-border bg-white p-5 text-sm font-medium text-navy/80">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald2" /> {x}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <SponsorProposalBand title="Exhibiting is step one. Sponsoring puts you on stage." />

      <section className="relative overflow-hidden bg-[#0A1B33] grain py-20 text-center">
        <div className="relative mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Put your technology on the floor.</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <CTAButton to="/exhibit">Book an Exhibitor Stall</CTAButton>
              <LeadDialog
                formType="brochure"
                title="Download the Expo Brochure"
                ctaLabel="Get the brochure"
                successMsg="Sent. Your download is starting now."
                downloadUrl={DOCS.brochure.url}
                downloadName={DOCS.brochure.name}
                trigger={<CTAButton variant="outline" className="text-white">Download the Expo Brochure</CTAButton>}
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
